import isEmpty from 'lodash/isEmpty';
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Helmet} from 'react-helmet';
import compose from 'recompose/compose';
import moment from 'moment-timezone';
import EnvironmentUtils from '@spothero/utils/environment';
import BranchUtils from 'utils/branch';
import WithConfig from 'config/WithConfig';

const isMobile = EnvironmentUtils.isMobileDevice();

const SearchMeta = ({
    siteUrl,
    displayDateFormat,
    standardTimeFormat,
    parkingNearMeTitle,
    parkingNearMeDescription,
    searchType,
    geolocate,
    isMonthly,
    searchRequestLatitude,
    searchRequestLongitude,
    starts,
    ends,
    searchString,
    event,
    city,
    destination
}) => {
    const hasCity = !isEmpty(city);
    const hasDestination = !isEmpty(destination);
    const hasEvent = !isEmpty(event);
    const isCity = (searchType === 'city');
    const isEvent = (searchType === 'event');
    const isDestination = (searchType === 'destination');
    const isVenue = (searchType === 'venue');
    const isVenueDestinationEvent = (isVenue || isDestination || isEvent);
    const place = (hasDestination && destination.google_places)
        ? destination.google_places[0]
        : null;
    const latitude = (isVenueDestinationEvent && hasDestination)
        ? destination.latitude
        : (isCity && hasCity)
            ? city.latitude
            : searchRequestLatitude;
    const longitude = (isVenueDestinationEvent && hasDestination)
        ? destination.longitude
        : (isCity && hasCity)
            ? city.longitude
            : searchRequestLongitude;
    const mobileDeepLinkParams = BranchUtils.generateDeepLinkParams({latitude, longitude, searchString, place});
    let nativeAppDeepLink = BranchUtils.generateDeepLink({latitude, longitude, searchString, place, starts, ends});
    let canonicalUrl = null;
    let title = null;
    let description = null;
    let extraSchema = null;

    if (isEvent && hasEvent) {
        const eventStartDay = moment(event.starts).format(displayDateFormat);
        const eventStartTime = moment(event.starts).format(standardTimeFormat);

        nativeAppDeepLink = BranchUtils.generateDeepLink({latitude, longitude, searchString, place, starts: event.starts, ends: event.ends});
        title = (!isEmpty(event.meta_title))
            ? event.meta_title
            : `${event.title} Parking (${eventStartDay}) | SpotHero`;
        description = (!isEmpty(event.meta_description))
            ? event.meta_description
            : `Find parking near ${destination.title} for ${event.title} on ${eventStartDay} at ${eventStartTime}.`;
        extraSchema = {
            '@context': 'http://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [{
                '@type': 'ListItem',
                position: 1,
                item: {
                    '@type': 'City',
                    name: city.display_name,
                    url: `${siteUrl}/${city.relative_url}`
                }
            },
            {
                '@type': 'ListItem',
                position: 2,
                item: {
                    name: 'Venues',
                    url: `${siteUrl}/${city.slug}/theaters-and-venues-parking`
                }
            }]
        };
    } else if (isDestination && hasDestination) {
        canonicalUrl = (isMonthly && destination.monthly_relative_url && destination.monthly_enabled)
            ? `${siteUrl}/${city.slug}/${destination.parent_url || destination.monthly_relative_url}`
            : `${siteUrl}/${city.slug}/${destination.parent_url || destination.relative_url}`;
        title = (isMonthly && destination.monthly_enabled)
            ? (!isEmpty(destination.monthly_meta_title))
                ? destination.monthly_meta_title
                : `${destination.title} Monthly Parking - Reserve & Save | SpotHero`
            : (!isEmpty(destination.meta_title))
                ? destination.meta_title
                : `${destination.title} Parking - Reserve & Save | SpotHero`;
        description = (isMonthly && destination.monthly_enabled)
            ? (!isEmpty(destination.monthly_meta_description))
                ? destination.monthly_meta_description
                : `Find great ${destination.title} monthly parking through SpotHero. Shop for monthly parking in ${destination.title} and book the best rates with SpotHero.`
            : (!isEmpty(destination.meta_description))
                ? destination.meta_description
                : `Reserve ${destination.title} parking through SpotHero. Book parking near ${destination.title} and save with SpotHero.`;
        extraSchema = {
            '@context': 'http://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [{
                '@type': 'ListItem',
                position: 1,
                item: {
                    '@type': 'City',
                    name: `${city.display_name}`,
                    url: `${siteUrl}/${city.relative_url}`

                }
            }]
        };
    } else if (isVenue && hasDestination) {
        canonicalUrl = `${siteUrl}/${city.slug}/${destination.relative_url}`;
        title = (!isEmpty(destination.meta_title))
            ? destination.meta_title
            : `${destination.title} Parking, Find a ${(hasCity) ? `${city.display_name}` : ''} Parking Garage near ${destination.title}.`;
        description = (!isEmpty(destination.meta_description))
            ? destination.meta_description
            : `${destination.title} parking options. Use SpotHero to search for parking spots in lots and garages near ${(hasCity) ? `${city.display_name}'s` : ''} ${destination.title}.`;
        extraSchema = [
            {
                '@context': 'http://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [{
                    '@type': 'ListItem',
                    position: 1,
                    item: {
                        '@type': 'City',
                        name: `${city.display_name}`,
                        url: `${siteUrl}/${city.relative_url}`
                    }
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    item: {
                        name: 'Venues',
                        url: `${siteUrl}/${city.slug}/theaters-and-venues-parking`
                    }
                }]
            },
            {
                '@context': 'http://schema.org',
                '@type': 'Place',
                url: `${siteUrl}/${city.slug}/${destination.relative_url}`,
                name: `${destination.title}`,
                address: {
                    '@type': 'PostalAddress',
                    streetAddress: `${destination.street_address}`,
                    addressLocality: `${destination.city}`,
                    addressRegion: `${destination.state}`
                },
                geo: {
                    '@type': 'GeoCoordinates',
                    latitude: `${destination.latitude}`,
                    longitude: `${destination.longitude}`
                },
                containedInPlace: {
                    '@type': 'City',
                    name: `${city.display_name}`,
                    url: `${siteUrl}/${city.relative_url}`
                }
            }
        ];
    } else if (isCity && hasCity) {
        canonicalUrl = (isMonthly && city.monthly_relative_url)
            ? `${siteUrl}/${city.monthly_relative_url}`
            : `${siteUrl}/${city.relative_url}`;
        title = (isMonthly && !isEmpty(city.monthly_meta_title))
            ? city.monthly_meta_title
            : (!isEmpty(city.transient_meta_title))
                ? city.transient_meta_title
                : `${city.display_name} Parking - Save Up to 50% | SpotHero`;
        description = (isMonthly && !isEmpty(city.monthly_meta_description))
            ? city.monthly_meta_description
            : (!isEmpty(city.transient_meta_description))
                ? city.transient_meta_description
                : `${city.display_name} Parking - Find, compare & reserve parking near ${city.display_name}. Book guaranteed parking online & save with SpotHero.`;
    } else {
        canonicalUrl = (geolocate)
            ? (isMonthly)
                ? `${siteUrl}/monthly-parking-near-me`
                : `${siteUrl}/parking-near-me`
            : null;
        title = (geolocate && parkingNearMeTitle)
            ? parkingNearMeTitle
            : (isMonthly)
                ? `${(hasCity) ? `${city.display_name}` : 'SpotHero'} Monthly Parking | SpotHero`
                : `${(hasCity) ? city.display_name : 'SpotHero'} Parking - Save Up to 50% | SpotHero`;
        description = (geolocate && parkingNearMeDescription)
            ? parkingNearMeDescription
            : (isMonthly)
                ? `Find ${(hasCity) ? city.display_name : 'SpotHero'} monthly parking. Search for garages & lots using our parking map. Get up to 50% off when you book online with SpotHero.`
                : `${(hasCity) ? city.display_name : 'SpotHero'} Parking - Find, compare & reserve parking. Book guaranteed parking online & save with SpotHero.`;
        extraSchema = {
            '@context': 'http://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [{
                '@type': 'ListItem',
                position: 1,
                item: {
                    '@type': 'City',
                    name: `${city.display_name}`,
                    url: `${canonicalUrl}`
                }
            }]
        };
    }

    if (isMobile) {
        BranchUtils.setViewData({data: {$canonical_url: nativeAppDeepLink}}); // eslint-disable-line camelcase
    }

    return (
        <Helmet>
            {/* eslint-disable react/jsx-max-props-per-line */}
            {title &&
                <title>{title}</title>
            }
            {description &&
                <meta name="description" itemProp="description" content={description} />
            }
            {canonicalUrl &&
                <link rel="canonical" href={canonicalUrl} />
            }
            {(!hasDestination && !window.location.pathname.match(/parking-near-me/) && !isCity) &&
                <meta name="robots" content="noindex, follow" />
            }
            {(isVenueDestinationEvent || isCity) &&
                <link rel="alternate" href={`android-app://com.spothero.spothero/spothero/com.spothero/search/${mobileDeepLinkParams}`} />
            }
            {(isVenueDestinationEvent || isCity) &&
                <link rel="alternate" href={`ios-app://499097243/spothero/search/${mobileDeepLinkParams}`} />
            }
            {(isVenueDestinationEvent || isCity) &&
                <meta property="al:ios:url" content={`spothero://search${mobileDeepLinkParams}`} />
            }
            {(isVenueDestinationEvent || isCity) &&
                <meta property="al:ios:app_store_id" content="499097243" />
            }
            {(isVenueDestinationEvent || isCity) &&
                <meta property="al:ios:app_name" content="SpotHero" />
            }
            {(isVenueDestinationEvent || isCity) &&
                <meta property="al:android:url" content={`spothero://search${mobileDeepLinkParams}`} />
            }
            {(isVenueDestinationEvent || isCity) &&
                <meta property="al:android:app_name" content="SpotHero" />
            }
            {(isVenueDestinationEvent || isCity) &&
                <meta property="al:android:package" content="com.spothero.spothero" />
            }
            {hasDestination &&
                <meta property="al:web:url" content={canonicalUrl} />
            }
            {extraSchema &&
                <script type="application/ld+json">
                    {JSON.stringify(extraSchema)}
                </script>
            }
            {/* eslint-enable react/jsx-max-props-per-line */}
        </Helmet>
    );
};

SearchMeta.propTypes = {
    siteUrl: PropTypes.string.isRequired,
    displayDateFormat: PropTypes.string.isRequired,
    standardTimeFormat: PropTypes.string.isRequired,
    parkingNearMeTitle: PropTypes.string,
    parkingNearMeDescription: PropTypes.string,
    searchType: PropTypes.string,
    geolocate: PropTypes.bool,
    isMonthly: PropTypes.bool,
    searchRequestLatitude: PropTypes.number,
    searchRequestLongitude: PropTypes.number,
    starts: PropTypes.string,
    ends: PropTypes.string,
    searchString: PropTypes.string,
    city: PropTypes.object,
    event: PropTypes.object,
    destination: PropTypes.object
};

const mapStateToProps = state => {
    const {
        search: {
            data: {
                searchType
            }
        },
        searchRequest: {
            monthly: isMonthly,
            latitude: searchRequestLatitude,
            longitude: searchRequestLongitude,
            starts,
            ends,
            search_string: searchString,
            geolocate
        },
        city: {
            data: city
        },
        destination: {
            data: destination
        },
        event: {
            data: event
        }
    } = state;

    return {
        searchType,
        geolocate,
        isMonthly,
        searchRequestLatitude,
        searchRequestLongitude,
        starts,
        ends,
        searchString,
        event,
        city,
        destination
    };
};

const enhance = compose(
    WithConfig,
    connect(mapStateToProps)
);

export default enhance(SearchMeta);
